import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  FileText,
  TrendingUp,
  AlertTriangle,
  History,
  Users,
  Sparkles,
  Award,
  Home,
  Settings,
  LogOut,
  Plus,
} from 'lucide-react';
import { useEscapeKey } from '../hooks/useKeyboardNav';
import './CommandPalette.css';

const CommandPalette = ({ user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const dialogRef = useRef(null);

  // Close on Escape
  useEscapeKey(() => setOpen(false), open);

  useEffect(() => {
    const down = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const commands = [
    {
      group: 'Navegación',
      items: [
        { icon: Home, label: 'Dashboard', shortcut: 'D', action: () => navigate('/dashboard') },
        { icon: FileText, label: 'Documentos', shortcut: 'O', action: () => navigate('/documents') },
        { icon: TrendingUp, label: 'Escucha Social', shortcut: 'S', action: () => navigate('/social') },
        { icon: AlertTriangle, label: 'Alertas', shortcut: 'A', action: () => navigate('/alerts') },
        { icon: History, label: 'Historial', shortcut: 'H', action: () => navigate('/history') },
        { icon: Users, label: 'Stakeholders', shortcut: 'K', action: () => navigate('/stakeholders') },
        { icon: AlertTriangle, label: 'Crisis Simulator', shortcut: 'C', action: () => navigate('/crisis-simulator') },
        { icon: Award, label: 'Media Impact', shortcut: 'M', action: () => navigate('/media-impact') },
        { icon: Sparkles, label: 'Virality Predictor', shortcut: 'V', action: () => navigate('/virality-predictor') },
        { icon: Sparkles, label: 'Auto-Response', shortcut: 'R', action: () => navigate('/auto-response') },
        { icon: Sparkles, label: 'IA Tools', shortcut: 'I', action: () => navigate('/ai-tools') },
        { icon: Award, label: 'Avanzado', shortcut: 'Z', action: () => navigate('/advanced') },
      ],
    },
    {
      group: 'Acciones',
      items: [
        {
          icon: Plus,
          label: 'Generar Documento',
          shortcut: 'N',
          action: () => {
            navigate('/documents');
            setTimeout(() => {
              const button = document.querySelector('[data-testid="generate-button"]');
              if (button) button.click();
            }, 100);
          },
        },
        {
          icon: Plus,
          label: 'Agregar Stakeholder',
          action: () => {
            navigate('/stakeholders');
            setTimeout(() => {
              const button = document.querySelector('[data-testid="add-stakeholder-button"]');
              if (button) button.click();
            }, 100);
          },
        },
      ],
    },
    {
      group: 'Cuenta',
      items: [
        { icon: Settings, label: 'Configuración', action: () => {} },
        { icon: LogOut, label: 'Cerrar Sesión', action: onLogout },
      ],
    },
  ];

  const handleSelect = (action) => {
    action();
    setOpen(false);
    setSearch('');
  };

  return (
    <>
      {/* Trigger Button */}
      <button
        onClick={() => setOpen(true)}
        className="hidden md:flex items-center gap-2 px-3 py-2 text-sm text-gray-400 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors"
      >
        <Search className="w-4 h-4" />
        <span>Buscar...</span>
        <kbd className="ml-auto px-2 py-0.5 text-xs bg-white/10 rounded">⌘K</kbd>
      </button>

      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
            />

            {/* Command Palette */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="fixed top-[15%] left-1/2 -translate-x-1/2 w-full max-w-2xl z-50 px-4"
            >
              <Command
                ref={dialogRef}
                className="command-palette glass-strong rounded-2xl overflow-hidden shadow-2xl"
                value={search}
                onValueChange={setSearch}
              >
                <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
                  <Search className="w-5 h-5 text-gray-400" />
                  <Command.Input
                    placeholder="Buscar o ejecutar comando..."
                    className="flex-1 bg-transparent text-white placeholder:text-gray-500 outline-none text-lg"
                    autoFocus
                  />
                  <kbd className="px-2 py-1 text-xs text-gray-400 bg-white/5 rounded">ESC</kbd>
                </div>

                <Command.List className="max-h-[400px] overflow-y-auto p-2">
                  <Command.Empty className="py-12 text-center text-gray-500">
                    No se encontraron resultados
                  </Command.Empty>

                  {commands.map((group, groupIndex) => (
                    <Command.Group
                      key={`group-${groupIndex}`}
                      heading={group.group}
                      className="mb-4 last:mb-0"
                    >
                      <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {group.group}
                      </div>
                      {group.items.map((item, itemIndex) => (
                        <Command.Item
                          key={`cmd-${itemIndex}`}
                          onSelect={() => handleSelect(item.action)}
                          className="flex items-center justify-between px-3 py-3 rounded-lg cursor-pointer text-gray-300 hover:bg-white/10 hover:text-white transition-colors mb-1 group"
                        >
                          <div className="flex items-center gap-3">
                            <item.icon className="w-5 h-5 text-gray-400 group-hover:text-purple-400 transition-colors" />
                            <span>{item.label}</span>
                          </div>
                          {item.shortcut && (
                            <kbd className="px-2 py-1 text-xs text-gray-500 bg-white/5 rounded group-hover:bg-white/10 group-hover:text-gray-400">
                              {item.shortcut}
                            </kbd>
                          )}
                        </Command.Item>
                      ))}
                    </Command.Group>
                  ))}
                </Command.List>

                <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 text-xs text-gray-500">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 bg-white/5 rounded">↑</kbd>
                      <kbd className="px-1.5 py-0.5 bg-white/5 rounded">↓</kbd>
                      <span className="ml-1">navegar</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <kbd className="px-1.5 py-0.5 bg-white/5 rounded">↵</kbd>
                      <span className="ml-1">ejecutar</span>
                    </div>
                  </div>
                  {user && (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center text-xs">
                        {user.name[0]}
                      </div>
                      <span>{user.name}</span>
                    </div>
                  )}
                </div>
              </Command>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default CommandPalette;
